import type { Database } from '@/types/database.generated';
import { CONSENT_TYPES, PORTAL_RECORDABLE_CONSENT_TYPES, consentTypeIsRefusal, type ConsentTypeKey } from './consentTypes';

type CurrentRow = Database['public']['Views']['patient_consent_current']['Row'];

export type ConsentDisplayStatus = 'given' | 'refused' | 'withdrawn' | 'expired' | 'not_set';

export interface ConsentStatusRow {
  key: string;
  consentType: ConsentTypeKey;
  status: ConsentDisplayStatus;
  current: CurrentRow | null;
}

export function toDisplayStatus(effective: string | null | undefined): ConsentDisplayStatus {
  switch (effective) {
    case 'given':
    case 'refused':
    case 'withdrawn':
    case 'expired':
      return effective;
    default:
      return 'not_set';
  }
}

/**
 * One row per recorded type + scope, ordered like CONSENT_TYPES, plus a
 * "not set" row for every portal-recordable type without any record.
 */
export function buildConsentStatusRows(current: readonly CurrentRow[]): ConsentStatusRow[] {
  const order = (type: ConsentTypeKey) => CONSENT_TYPES.indexOf(type);
  const recorded: ConsentStatusRow[] = current
    .filter((row): row is CurrentRow & { consent_type: ConsentTypeKey } => row.consent_type !== null)
    .map((row) => ({
      key: `${row.consent_type}:${row.scope_key ?? ''}`,
      consentType: row.consent_type,
      status: toDisplayStatus(row.effective_status),
      current: row,
    }));
  const seen = new Set(recorded.map((row) => row.consentType));
  const placeholders: ConsentStatusRow[] = PORTAL_RECORDABLE_CONSENT_TYPES.filter((type) => !seen.has(type)).map(
    (type) => ({ key: `${type}:none`, consentType: type, status: 'not_set', current: null }),
  );
  return [...recorded, ...placeholders].sort((a, b) => {
    const diff = order(a.consentType) - order(b.consentType);
    if (diff !== 0) return diff;
    return (b.current?.decided_at ?? '').localeCompare(a.current?.decided_at ?? '');
  });
}

export function consentStatusLabelKey(type: ConsentTypeKey, status: ConsentDisplayStatus): string {
  if (status === 'given' && consentTypeIsRefusal(type)) return 'consent.decision.refusal_given';
  if (status === 'withdrawn' && consentTypeIsRefusal(type)) return 'consent.decision.refusal_lifted';
  return `consent.decision.${status}`;
}
